import React, { useEffect, useRef, useState } from 'react';
import { Button, Checkbox, CircularProgress, FormControlLabel, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import {
  getOAuthAccessToken,
  integrationsApi,
  tasksApi,
  useInitTodoIntegration,
} from '../../../data-access';
import './TrelloAuthCallback.scss';
import { Path } from '../../../routing/path';
import { trackEventMixpanel } from '../../../utils';
import { TodoTasksBoard } from '@demind-inc/core';

interface TrelloBoard {
  id: string;
  name: string;
  desc?: string | null;
}

const TrelloAuthCallback: React.FC = () => {
  const { initTodoIntegration } = useInitTodoIntegration();
  const navigate = useNavigate();
  const hasExchanged = useRef(false);

  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [userId, setUserId] = useState<string>('');
  const [isMobileApp, setIsMobileApp] = useState(false);
  const [redirectDestination, setRedirectDestination] = useState<string>(Path.DASHBOARD);

  const [boards, setBoards] = useState<Partial<TodoTasksBoard>[]>([]);
  const [selectedBoardIds, setSelectedBoardIds] = useState<Set<string>>(new Set());

  const [isExchangeInFlight, setIsExchangeInFlight] = useState(false);
  const [isFetchingBoards, setIsFetchingBoards] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [errorState, setErrorState] = useState<{ message: string; fatal: boolean } | null>(null);

  useEffect(() => {
    const exchange = async () => {
      const currentUrl = window.location.href;
      const token = currentUrl.split('#token=')?.[1];

      if (!token) {
        navigate(Path.DASHBOARD);
        return;
      }

      // Prevent multiple calls
      if (hasExchanged.current) return;
      hasExchanged.current = true;

      setIsExchangeInFlight(true);
      setErrorState(null);

      try {
        const params = new URLSearchParams(new URL(currentUrl).search);
        const redirectTo = params.get('redirectTo') || Path.DASHBOARD;
        const stateUserId = params.get('userId') || '';
        const mobileFlag = params.get('isMobileApp')?.includes('true') ?? false;

        setRedirectDestination(redirectTo);
        setIsMobileApp(mobileFlag);
        setUserId(stateUserId);

        if (!stateUserId) {
          throw new Error('Missing user information.');
        }

        setAccessToken(token);
      } catch (error) {
        console.error('Error during Trello authentication:', error);
        setErrorState({ message: "Couldn't connect to Trello — try again?", fatal: true });
        hasExchanged.current = false;
      } finally {
        setIsExchangeInFlight(false);
      }
    };

    exchange();
  }, [navigate]);

  useEffect(() => {
    if (!accessToken) {
      return;
    }

    let isActive = true;
    setIsFetchingBoards(true);

    integrationsApi
      .getOriginalTodoBoards('trello', accessToken)
      .then(({ data }) => {
        if (!isActive) return;
        const fetchedBoards = data as Partial<TodoTasksBoard>[];
        setBoards(fetchedBoards);
        setSelectedBoardIds(
          new Set(
            fetchedBoards.filter((board) => !board.closed).map((board) => board.originalBoardId)
          )
        );

        // Fetch existing projects and pre-select boards that are already connected
        if (!userId) {
          return;
        }
        if (!getOAuthAccessToken()) {
          return [fetchedBoards, null] as const;
        }
        return tasksApi
          .getTodoProjects(userId)
          .then((response) => [fetchedBoards, response] as const)
          .catch((error) => {
            console.warn('Failed to fetch existing Trello projects', error);
            return [fetchedBoards, null] as const;
          });
      })
      .then((result) => {
        if (!isActive || !result) return;
        const [fetchedBoards, response] = result;
        const projects = (response?.data as TodoTasksBoard[]) || [];
        const trelloProjects = projects.filter(
          (project) => project.appFrom === 'trello' && !project.closed
        );
        const existingBoardIds = new Set(
          trelloProjects.map((project) => project.originalBoardId).filter(Boolean) as string[]
        );

        // Select boards that match existing originalBoardIds
        const boardsToSelect = fetchedBoards
          .map((board) => board.originalBoardId)
          .filter((id) => id && existingBoardIds.has(id)) as string[];

        if (boardsToSelect.length > 0) {
          setSelectedBoardIds(new Set(boardsToSelect));
        }
      })
      .catch((error) => {
        console.error('Failed to fetch Trello boards', error);
        if (!isActive) return;
        setErrorState({ message: "Couldn't load your Trello boards.", fatal: true });
      })
      .finally(() => {
        if (!isActive) return;
        setIsFetchingBoards(false);
      });

    return () => {
      isActive = false;
    };
  }, [accessToken, userId]);

  const handleToggleSelectAll = () => {
    setSelectedBoardIds((prev) => {
      if (!boards.length) {
        return prev;
      }

      if (prev.size === boards.length) {
        return new Set();
      }

      return new Set(boards.map((board) => board.originalBoardId));
    });
  };

  const handleToggleBoard = (boardId: string) => {
    setSelectedBoardIds((prev) => {
      const next = new Set(prev);

      if (next.has(boardId)) {
        next.delete(boardId);
      } else {
        next.add(boardId);
      }

      return next;
    });
  };

  const handleSync = async () => {
    if (!userId) {
      setErrorState({ message: "We're missing some account info — try connecting again.", fatal: true });
      return;
    }

    const boardIds = Array.from(selectedBoardIds);

    if (!boardIds.length) {
      return;
    }

    try {
      setErrorState(null);
      setIsSyncing(true);
      await initTodoIntegration({
        token: accessToken,
        todoType: 'trello',
        userId: userId,
        shouldSyncTasks: false,
        skipRefresh: isMobileApp,
        originalBoardIds: boardIds,
      });

      trackEventMixpanel('connect_todo_auth_done', {
        todoType: 'trello',
        fromMobileApp: isMobileApp,
        boardsSelected: boardIds.length,
      });

      if (isMobileApp) {
        window.location.href = 'lifestack://todo-callback';
        return;
      }

      navigate(redirectDestination || Path.DASHBOARD);
    } catch (error) {
      console.error('Failed to trigger Trello sync', error);
      setErrorState({
        message: "Couldn't start syncing those boards.",
        fatal: false,
      });
    } finally {
      setIsSyncing(false);
    }
  };

  if (isExchangeInFlight && !accessToken && !errorState) {
    return (
      <div className="trello-auth-callback">
        <CircularProgress />
      </div>
    );
  }

  const allSelected = boards.length > 0 && selectedBoardIds.size === boards.length;
  const isIndeterminate = selectedBoardIds.size > 0 && !allSelected;
  const fatalError = errorState?.fatal;
  const errorMessage = errorState?.message;

  return (
    <div className="trello-auth-callback">
      <div className="trello-auth-callback__card">
        <Typography variant="h5" component="h1" className="trello-auth-callback__title">
          Select Trello boards
        </Typography>

        {fatalError ? (
          <div className="trello-auth-callback__error">
            <Typography color="error" variant="body2" className="trello-auth-callback__error-text">
              {errorMessage}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate(redirectDestination || Path.DASHBOARD)}
            >
              Back to dashboard
            </Button>
          </div>
        ) : (
          <div className="trello-auth-callback__body">
            {errorMessage ? (
              <Typography
                color="error"
                variant="body2"
                className="trello-auth-callback__error-text"
              >
                {errorMessage}
              </Typography>
            ) : null}

            <Typography variant="body2" className="trello-auth-callback__description">
              Choose the boards you want Lifestack to import tasks from.
            </Typography>

            {boards.length > 0 && (
              <FormControlLabel
                control={
                  <Checkbox
                    indeterminate={isIndeterminate}
                    checked={allSelected}
                    onChange={handleToggleSelectAll}
                  />
                }
                label="Select all boards"
                className="trello-auth-callback__select-all"
              />
            )}

            <div className="trello-auth-callback__list">
              {isFetchingBoards ? (
                <div className="trello-auth-callback__loading">
                  <CircularProgress size={24} />
                </div>
              ) : boards.length ? (
                boards.map((board) => {
                  const isChecked = selectedBoardIds.has(board.originalBoardId);

                  return (
                    <label
                      key={board.originalBoardId}
                      className="trello-auth-callback__board"
                      htmlFor={`board-${board.originalBoardId}`}
                    >
                      <Checkbox
                        id={`board-${board.originalBoardId}`}
                        checked={isChecked}
                        onChange={() => handleToggleBoard(board.originalBoardId)}
                      />
                      <div className="trello-auth-callback__board-info">
                        <span className="trello-auth-callback__board-name">{board.name}</span>
                      </div>
                    </label>
                  );
                })
              ) : (
                <Typography variant="body2" className="trello-auth-callback__empty">
                  No boards available for import.
                </Typography>
              )}
            </div>

            <div className="trello-auth-callback__actions">
              <Button
                variant="contained"
                color="primary"
                onClick={handleSync}
                disabled={selectedBoardIds.size === 0 || isSyncing || !!fatalError}
                className="trello-auth-callback__sync-button"
              >
                {isSyncing ? (
                  <>
                    <CircularProgress size={18} className="trello-auth-callback__button-spinner" />
                    Syncing boards
                  </>
                ) : (
                  'Import selected boards'
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrelloAuthCallback;
